import { useState } from "react";
import {
  verifyTrail,
  unverifyTrail,
  deleteTrail,
  ApiError,
} from "../api";

/**
 * Floating batch-action bar — Phase 3.
 *
 * Pinned to the bottom of the viewport while the curator has trails
 * selected. Fires verify / unverify / delete sequentially against the
 * worker; the parent workspace mirrors the resulting changes in its
 * in-memory snapshot.
 *
 * Destructive copy stays literal (Delete · Are you sure?) per
 * Peak-End Rule — the playful trail slang we use elsewhere is muted
 * around irreversible actions so nobody accidentally nukes a row.
 *
 * Respects safe-area-inset-bottom so the bar clears the iOS home
 * indicator on phones.
 */
export type BatchOutcome =
  | { kind: "verify"; slug: string; ok: boolean; error?: string }
  | { kind: "unverify"; slug: string; ok: boolean; error?: string }
  | { kind: "delete"; slug: string; ok: boolean; error?: string };

export function BatchActionBar({
  selectedSlugs,
  onCancel,
  onComplete,
}: {
  selectedSlugs: string[];
  onCancel: () => void;
  /** Fired once after all sub-requests resolve, with the per-slug
   *  outcomes. The parent uses these to patch its in-memory list
   *  (mark verified, remove deleted rows, etc.). */
  onComplete: (outcomes: BatchOutcome[]) => void;
}) {
  const [working, setWorking] = useState<null | "verify" | "unverify" | "delete">(
    null
  );
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );

  async function runBatch(
    kind: "verify" | "unverify" | "delete",
    op: (slug: string) => Promise<unknown>
  ) {
    setWorking(kind);
    setProgress({ done: 0, total: selectedSlugs.length });
    const outcomes: BatchOutcome[] = [];
    for (let i = 0; i < selectedSlugs.length; i++) {
      const slug = selectedSlugs[i];
      try {
        await op(slug);
        outcomes.push({ kind, slug, ok: true });
      } catch (err) {
        outcomes.push({
          kind,
          slug,
          ok: false,
          error:
            err instanceof ApiError ? err.message : "Request failed.",
        });
      }
      setProgress({ done: i + 1, total: selectedSlugs.length });
    }
    setWorking(null);
    setProgress(null);
    onComplete(outcomes);
  }

  async function handleDelete() {
    const n = selectedSlugs.length;
    const ok = confirm(
      `Delete ${n} trail${n === 1 ? "" : "s"}? This can't be undone.`
    );
    if (!ok) return;
    await runBatch("delete", (slug) => deleteTrail(slug));
  }

  const n = selectedSlugs.length;
  const busy = working != null;

  return (
    <div className="batch-bar" role="region" aria-label="Batch actions">
      <div className="batch-bar-inner">
        <div className="batch-bar-count">
          {progress ? (
            <span>
              {working === "delete"
                ? "Deleting"
                : working === "verify"
                  ? "Verifying"
                  : "Unverifying"}{" "}
              {progress.done} / {progress.total}…
            </span>
          ) : (
            <span>
              <strong>{n}</strong> selected
            </span>
          )}
        </div>
        <div className="batch-bar-actions">
          <button
            type="button"
            className="small"
            disabled={busy}
            onClick={() => runBatch("verify", (slug) => verifyTrail(slug))}
          >
            Verify
          </button>
          <button
            type="button"
            className="small secondary"
            disabled={busy}
            onClick={() => runBatch("unverify", (slug) => unverifyTrail(slug))}
          >
            Unverify
          </button>
          <button
            type="button"
            className="small danger"
            disabled={busy}
            onClick={handleDelete}
          >
            Delete
          </button>
          <button
            type="button"
            className="small secondary"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
